import React, { Component } from 'react';
import cm from 'helpers/commons';
import { Popup, Table } from 'semantic-ui-react';
import { FixedSizeList } from 'react-window';
import DetailSikdaeResultModal from './Modal/DetailSikdaeResultModal';

const ROW_HEIGHT = 49;
const LOAD_MORE_THRESHOLD = 6;
const OVERSCAN_COUNT = 8;
const MIN_COLUMN_WIDTH = 120;
const EMPTY_COLUMN_WIDTH = 80;
const MAX_COLUMN_WIDTH = 360;
const CHAR_PIXEL_WIDTH = 9;
const CELL_HORIZONTAL_PADDING = 32;
const RIGHT_ALIGNED_CELL_PADDING = 20;
const MIN_LIST_HEIGHT = 280;
const MAX_LIST_HEIGHT = 720;
const LIST_VIEWPORT_OFFSET = 340;

const VirtualListOuter = React.forwardRef(({ style, ...props }, ref) => (
  <div
    ref={ref}
    {...props}
    style={{
      ...style,
      overflowX: 'hidden',
      overflowY: 'auto'
    }}
  />
));

VirtualListOuter.displayName = 'VirtualListOuter';

class DetailSikdaeResultSamsungSds extends Component {
  constructor(props) {
    super(props);
    this.state = {
      listHeight: MIN_LIST_HEIGHT,
      containerWidth: 0
    };
    this.containerRef = React.createRef();
  }

  componentDidMount() {
    this.updateListHeight();
    window.addEventListener('resize', this.updateListHeight);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateListHeight);
  }

  updateListHeight = () => {
    const viewportHeight = window.innerHeight || MIN_LIST_HEIGHT;
    const containerRect = this.containerRef.current ? this.containerRef.current.getBoundingClientRect() : null;
    const containerTop = containerRect ? containerRect.top : 0;
    const containerWidth = containerRect ? containerRect.width : 0;
    const availableHeight = viewportHeight - containerTop - 80;
    const fallbackHeight = viewportHeight - LIST_VIEWPORT_OFFSET;
    const nextHeight = Math.max(availableHeight, fallbackHeight, MIN_LIST_HEIGHT);

    this.setState({
      listHeight: Math.min(nextHeight, MAX_LIST_HEIGHT),
      containerWidth
    });
  };

  makeHeader = () => [
    { data: '결제번호', textAlign: 'left' },
    { data: '날짜', textAlign: 'left' },
    { data: 'ID', textAlign: 'left' },
    { data: '이름', textAlign: 'left' },
    { data: '사원번호', textAlign: 'left' },
    { data: '부서1', textAlign: 'left' },
    { data: '부서2', textAlign: 'left' },
    { data: '부서3', textAlign: 'left' },
    { data: '가맹점', textAlign: 'left' },
    { data: '선택 정책 사용식대', textAlign: 'right' },
    { data: '총 결제총액', textAlign: 'right' },
    { data: '정산금액', textAlign: 'right' },
    { data: '조식', textAlign: 'right' },
    { data: '중식', textAlign: 'right' },
    { data: '석식', textAlign: 'right' },
    { data: '특식', textAlign: 'right' }
  ];

  formatNumber = (value) => {
    if (value || value === 0) {
      return cm.numberComma(value);
    }

    return '-';
  };

  makeRows = () => {
    const { detailRows = [], resultFn: { openModal } = {} } = this.props;
    const rightCell = (data) => ({
      data,
      textAlign: 'right',
      paddingRight: RIGHT_ALIGNED_CELL_PADDING
    });

    return detailRows.map((item) => ({
      row: [
        { data: item.payRoomIdx || '-', textAlign: 'left' },
        {
          data: item.usedate ? `${cm.yyyyMMdd(new Date(item.usedate))} ${cm.hhmmss(new Date(item.usedate))}` : '-',
          textAlign: 'left'
        },
        { data: item.signId || '-', textAlign: 'left' },
        { data: item.username || '-', textAlign: 'left' },
        { data: item.comidnum || '-', textAlign: 'left' },
        { data: item.orgName1 || '-', textAlign: 'left' },
        { data: item.orgName2 || '-', textAlign: 'left' },
        { data: item.orgName3 || '-', textAlign: 'left' },
        { data: item.storename || '-', textAlign: 'left' },
        rightCell(this.formatNumber(item.compoint)),
        rightCell(this.formatNumber(item.totalPaymentPrice)),
        rightCell(this.formatNumber(item.companyprice)),
        rightCell(this.formatNumber(item.breakfast)),
        rightCell(this.formatNumber(item.lunch)),
        rightCell(this.formatNumber(item.dinner)),
        rightCell(this.formatNumber(item.specialMeal))
      ],
      clickData: item.cid
    }));
  };

  handleItemsRendered = ({ visibleStopIndex }) => {
    const {
      detailRows = [],
      resultFn: { loadMorePartitions },
      hasMore,
      isFetchingMore
    } = this.props;

    if (!hasMore || isFetchingMore) {
      return;
    }

    if (visibleStopIndex >= detailRows.length - LOAD_MORE_THRESHOLD) {
      loadMorePartitions();
    }
  };

  renderColGroup = (headers, columnWidths) => (
    <colgroup>
      {headers.map((item, idx) => (
        <col key={`colgroup_${idx}`} style={{ width: `${columnWidths[idx] || MIN_COLUMN_WIDTH}px` }} />
      ))}
    </colgroup>
  );

  getCellTextLength = (value) => {
    if (value === null || value === undefined) {
      return 0;
    }

    return String(value).length;
  };

  getColumnWidths = (headers, rows) =>
    headers.map((header, headerIdx) => {
      if (rows.length === 0) {
        return EMPTY_COLUMN_WIDTH;
      }

      const headerLength = this.getCellTextLength(header.data);
      const rowMaxLength = rows.reduce((max, row) => {
        const cell = row.row[headerIdx];
        const cellLength = this.getCellTextLength(cell && cell.data);
        return Math.max(max, cellLength);
      }, 0);

      const nextWidth = Math.max(headerLength, rowMaxLength) * CHAR_PIXEL_WIDTH + CELL_HORIZONTAL_PADDING;

      return Math.min(Math.max(nextWidth, MIN_COLUMN_WIDTH), MAX_COLUMN_WIDTH);
    });

  getStatusMessage = () => {
    const { detailRows = [], hasMore, isFetchingMore } = this.props;

    if (isFetchingMore) {
      return '추가 데이터를 불러오는 중입니다.';
    }

    if (!hasMore && detailRows.length > 0) {
      return '모든 데이터를 불러왔습니다.';
    }

    return null;
  };

  renderHeader = (headers) => (
    <Table.Header>
      <Table.Row className="main">
        {headers.map((item, idx) => (
          <Table.HeaderCell
            key={`header_${idx}`}
            textAlign={item.textAlign || 'center'}
            style={{
              paddingRight: item.textAlign === 'right' ? RIGHT_ALIGNED_CELL_PADDING : null
            }}
          >
            {item.data}
          </Table.HeaderCell>
        ))}
      </Table.Row>
    </Table.Header>
  );

  renderVirtualRow = ({ index, style, data }) => {
    if (data.statusMessage && index === data.rows.length) {
      return (
        <div style={style}>
          <Table
            className="tb-common selectable"
            style={{ margin: 0, border: 'none', borderRadius: 0, tableLayout: 'fixed', width: '100%' }}
          >
            {data.renderColGroup(data.headers, data.columnWidths)}
            <Table.Body>
              <Table.Row>
                <Table.Cell colSpan={data.headers.length} textAlign="center">
                  {data.statusMessage}
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        </div>
      );
    }

    const item = data.rows[index];

    if (!item) {
      return null;
    }

    return (
      <div style={style}>
        <Table
          className="tb-common selectable"
          style={{ margin: 0, border: 'none', borderRadius: 0, tableLayout: 'fixed', width: '100%' }}
        >
          {data.renderColGroup(data.headers, data.columnWidths)}
          <Table.Body>
            <Table.Row>
              {item.row.map((col, colIdx) => (
                <Table.Cell
                  key={`col_${index}_${colIdx}`}
                  textAlign={col.textAlign || 'center'}
                  style={{
                    color: col.fontColor,
                    fontWeight: col.fontWeight,
                    textDecoration: col.textDecoration,
                    whiteSpace: col.whiteSpace || null,
                    background: col.background,
                    paddingRight: col.paddingRight
                  }}
                >
                  {col.isTooltip ? <Popup content={col.data} inverted trigger={<span>{col.data}</span>} /> : col.data}
                </Table.Cell>
              ))}
            </Table.Row>
          </Table.Body>
        </Table>
      </div>
    );
  };

  render() {
    const { detailRows = [], modalFn, hasMore, isFetchingMore, totalRow } = this.props;
    const { listHeight, containerWidth } = this.state;

    const headers = this.makeHeader();
    const rows = this.makeRows();
    const statusMessage = this.getStatusMessage();
    const itemCount = rows.length + (statusMessage ? 1 : 0);
    const columnWidths = this.getColumnWidths(headers, rows);
    const contentWidth = columnWidths.reduce((total, width) => total + width, 0);
    const tableMinWidth = Math.max(contentWidth, containerWidth);

    return (
      <div className="ds-result">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div>{totalRow ? `총 ${totalRow.toLocaleString()}건` : `현재 ${detailRows.length.toLocaleString()}건`}</div>
        </div>

        <div className="table-box" style={{ overflowX: 'auto' }} ref={this.containerRef}>
          <Table
            className="tb-common selectable"
            style={{ marginBottom: 0, width: `${tableMinWidth}px`, minWidth: `${tableMinWidth}px` }}
          >
            {this.renderColGroup(headers, columnWidths)}
            {this.renderHeader(headers)}
          </Table>

          {rows.length > 0 ? (
            <FixedSizeList
              height={listHeight}
              itemCount={itemCount}
              itemSize={ROW_HEIGHT}
              width={tableMinWidth}
              overscanCount={OVERSCAN_COUNT}
              itemData={{ rows, headers, columnWidths, renderColGroup: this.renderColGroup, statusMessage }}
              outerElementType={VirtualListOuter}
              onItemsRendered={this.handleItemsRendered}
            >
              {this.renderVirtualRow}
            </FixedSizeList>
          ) : (
            <Table
              className="tb-common selectable"
              style={{ marginTop: 0, width: `${tableMinWidth}px`, minWidth: `${tableMinWidth}px` }}
            >
              {this.renderColGroup(headers, columnWidths)}
              <Table.Body>
                <Table.Row>
                  <Table.Cell colSpan={headers.length} textAlign="center">
                    식대 상세조회 내역이 존재하지 않습니다.
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>
          )}
        </div>

        <DetailSikdaeResultModal modalFn={modalFn} />
      </div>
    );
  }
}

export default DetailSikdaeResultSamsungSds;
